<div class="flex flex-col w-full h-full">
  @if (modalLoading) {
    <div class="flex-grow w-full loader-wrap" @fadeIn>
      <div class="loader-sm is-mint"></div>
    </div>
  }
  @if (!modalLoading && selectedWorkflow === requestTypeEnum.accountSelection) {
    <header @fadeIn>
      <h2 class="m-10 mr-20 text-lg font-bold">New Service Request</h2>
      <button mat-button class="m-10 ml-auto icon-button is-mint is-inverted" type="button" mat-dialog-close>
        <i class="material-icons">close</i>
      </button>
    </header>
    <section class="m-10 dialog-content gap-y-15" @fadeIn>
      <div class="flex flex-col">
        <!-- ============ Step 1: which account, and which request ============

             Two questions on one screen had become two questions asked at once: choosing a
             request type made the solicitation list appear BELOW it, so the modal grew, began to
             scroll, and put the answer out of sight of the question. Splitting them means each
             screen asks one thing.

             Clicking a request advances on its own — see `chooseNavItem`. There is deliberately
             no Next here, so this step has no footer at all.

             🔴 AND THERE IS NO SECOND STEP ANY MORE (AndyP, 2026-10-02). It asked "who
             initiated this request?" and the answer is no longer collected or filed at all —
             `isSolicited` goes to CRM as NULL on every request type. The picker is one screen
             again, which is where it was before ACMTECH-968 split it. -->
        <span class="mb-10 font-bold required-label">
        Which account are you requesting an update for?</span>
        <form [formGroup]="searchField" class="relative flex justify-center">
          <input name="searchTextAccount" aria-label="Search Accounts input" matInput
            placeholder="Search for an account..." formControlName="searchInput" type="text"
            [matAutocomplete]="accountauto" class="w-full input is-lg" required>
          @if (selectedAccountId || searchField.controls.searchInput.value) {
            <button mat-button
              class="absolute right-0 h-full m-2 icon-button is-mint is-inverted text-gray-dark hover:bg-transparent"
              type="button" (click)="resetSearch()">
              <span class="material-icons mi-24">close</span>
            </button>
          }
        </form>
        <mat-autocomplete #accountauto="matAutocomplete" ngDefaultControl>
          @if (searchField.controls.searchInput.value && searchField.controls.searchInput.value.length > 2 && !selectedAccountId) {
            @if (!searchLoading) {
              @for (account of accounts; track account) {
                <mat-option class="flex"
                  [value]="account.custodian + (!!account.accountNumber ? ' - ' + account.accountNumber : '') + ' - ' + account.clients"
                  (click)="selectAccount(account)" @fadeIn>
                  <div class="flex">
                    <div class="flex flex-col pr-20">
                      <span class="mb-5 font-bold text-gray-dark" [textContent]="account.investmentAccountModel"></span>
                      <span class="text-gray">
                        {{account.custodian}}
                        {{account.accountNumber && '-'}}
                        {{account.accountNumber || ''}}
                        {{account.investmentAccountType && '-'}}
                        {{account.investmentAccountType || ''}}
                      </span>
                    </div>
                    <div class="ml-auto">
                      <span class="font-bold" [textContent]="account.clients"></span>
                    </div>
                  </div>
                </mat-option>
              }@empty {
                <mat-option class="bg-gray-lightest" readonly fadeIn>
                  <span class="text-sm text-gray">
                    No results found.
                  </span>
                </mat-option>
              }
            }
            @else{
              <mat-option readonly disabled @fadeIn>
                <div class="flex-grow w-full loader-wrap">
                  <div class="loader-sm is-mint"></div>
                </div>
              </mat-option>
            }
          }
        </mat-autocomplete>
        @if (searchField.invalid && searchField.touched) {
          <div class="mt-5 text-xs text-red">
            This field is required.
          </div>
        }
        <!-- Directly under the account field, because it is a fact about the account rather than
             about the request or about pressing Next. Shown for every request type.

             Gated on !eligibilityLoading so it appears in the same beat as the request-type list
             below rather than flashing in ahead of it. Absent entirely if the Eclipse read failed —
             no message has to mean "nothing to report", never "unrestricted".

             Orange with info_outline, not red with error_outline: the advisor is not blocked, and
             red is reserved for the things that do stop them. Note px-10 — 16 and 12 are not on the
             FIG spacing scale, so px-16/pr-12 would emit no CSS and the icon would sit flush
             against the border. -->
        @if (tradeRestrictionMessage && !eligibilityLoading) {
          <div class="flex items-start px-10 py-8 mt-10 text-sm border rounded gap-x-8 border-orange-lighter bg-orange-lightest text-orange-darker" @fadeIn>
            <!-- 1.6rem to keep the glyph proportional to text-sm; the 1.4rem variant pairs with
                 text-xs. Same pairing as the Do Not Trade attestation panel. -->
            <i class="material-icons" style="font-size: 1.6rem" aria-hidden="true">info_outline</i>
            <span>{{ tradeRestrictionMessage }}</span>
          </div>
        }
        <div class="flex flex-col">
          <!-- Held behind a loader until the account's sleeves come back, so options don't render
               dimmed and then light up once we know which ones apply. -->
          @if (eligibilityLoading) {
            <span class="mb-10 font-bold mt-10">Select the type of request:</span>
            <div class="w-full py-30 loader-wrap" @fadeIn>
              <div class="loader-sm is-mint"></div>
            </div>
          } @else {
          <!-- Promoted above the full list when the account looks like a new setup (one sleeve,
               no model yet or custom holdings). Everything else stays available below. -->
          @if (recommendedNavItems.length) {
            <span class="font-bold mt-10">Recommended for this account:</span>
            <span class="mb-10 text-sm text-gray-dark">This account has no model allocated yet.</span>
            <div class="flex flex-col w-full gap-y-5 mb-15">
              @for (navItem of recommendedNavItems; track navItem) {
                <button mat-button
                  class="button justify-start {{selectedWorkflowNavItem?.order === navItem.order ? 'is-outlined bg-mint-lightest font-bold ' : ''}} {{navItem.buttonClass}} is-inverted font-semibold flex items-center"
                  [matTooltip]="navItemTooltip(navItem)" matTooltipPosition="right"
                  matTooltipShowDelay="1000" (click)="chooseNavItem(navItem)">
                  <i class="mr-20 material-icons mi-22" [style.transform]="navItem.iconTransform" [textContent]="navItem.icon"></i>
                  <span [textContent]="navItem.title"></span>
                  @if (navItem.isNew) {
                    <span class="px-8 ml-10 text-xs font-bold tracking-wide uppercase rounded-full whitespace-nowrap bg-mint-lighter text-mint-darkest" [textContent]="navItem.newLabel ?? 'New Experience!'"></span>
                  }
                </button>
              }
            </div>
            <span class="mb-10 font-bold">Or select another type of request:</span>
          } @else {
            <span class="mb-10 font-bold mt-10">Select the type of request:</span>
          }
          <!-- ============ The main list, in two groups ============

               The V2 wizards on top in a soft mint panel; the long-standing requests below, plain
               and unlabelled. Eight flat rows with three NEW EXPERIENCE badges among them had
               become the hardest part of the modal to read, and the badges were carrying work a
               heading does better: repeated three times, the badge stops distinguishing anything.

               🔴 The PANEL carries the emphasis; the rows inside stay ordinary mint links. Filling
               each row solid mint was built and reverted: a solid fill means "this is the primary
               action", so three of them means none is — and they outshouted NEXT, the only real
               commit on this modal. Emphasis is relative, and containing three rows costs far
               less ink than flooding them. It also matches the house style, where highlighting is
               a light mint tint (table headers, badges, the active sidebar item) and solid mint
               with white text is reserved for controls that actually submit something.

               🟢 Only the NEW EXPERIENCE group is labelled. The panel already says where the
               group ends, so a second heading over the classic rows was naming a boundary the
               container had drawn — and "Classic requests" read as a demotion of two requests
               (Close the Account, General Account Message) that have no V2 counterpart and are
               not superseded by anything.

               ⚠️ The heading AND the panel appear only when both groups have contents (see
               `showNavGroupHeadings`). Without V2 permissions this is exactly the flat list it
               always was, and it goes back to being one when v1 is eventually retired — a panel
               drawn around every request in the menu would be chrome with nothing left to
               contrast against. The grouping is pilot scaffolding that removes itself. -->
          @if (showNavGroupHeadings) {
            <div class="flex items-center mt-5 mb-5 gap-x-10">
              <!-- "Guided workflows", not "New experience": the badge beside it already says new,
                   and a heading that repeats its own badge spends two elements on one word. This
                   says what the group IS — step-by-step wizards that collect the whole
                   instruction — and leaves the badge to carry the novelty. "Workflows" rather
                   than "requests" because every row in the menu is a request; the distinction
                   being drawn is how it is filled in. -->
              <span class="text-xs font-bold tracking-wide uppercase text-gray-dark">Guided workflows</span>
              <!-- ONE badge for the group, replacing one per row. -->
              <span class="px-8 text-xs font-bold tracking-wide uppercase rounded-full whitespace-nowrap bg-mint-lighter text-mint-darkest">New!</span>
              <span class="flex-grow border-b border-gray-lighter" aria-hidden="true"></span>
            </div>
          }
          <div class="flex flex-col w-full gap-y-5"
            [ngClass]="showNavGroupHeadings ? 'p-10 border border-solid rounded bg-mint-lightest border-mint-lighter' : 'mb-15'">
            @for (navItem of newNavItems; track navItem) {
              @if (navItem.order !== requestTypeEnum.planningDesk) {
                <!-- Unfilled inside the panel, so these read as choices rather than as three
                     primary buttons. Leaving the background alone also hands hover back to
                     `is-inverted`, whose `@apply`d fill an important utility would have suppressed.

                     Selected goes to `bg-white`, NOT the `bg-mint-lightest` the classic rows use —
                     that is the panel's own colour, so the picked row would vanish into it. White
                     reads as lifted off the tint, and the border names it.

                     ⚠️ `border-transparent` is kept OUT of the selected branch: utilities are
                     `!important` here (`@fig/tailwind-config`, `important: true`), so it would
                     override the border colour instead of sitting under it. `border-2
                     border-solid` is on every row in BOTH groups, so nothing changes height when
                     it is picked and the two groups stay aligned. -->
                <!-- ============ Two lines, not one ============

                     🔴 THE TITLES DO NOT SEPARATE THESE THREE on their own. All of them move money
                     or holdings, two of them touch cash, and one quietly also closes the account.
                     An advisor who cannot tell which tile reaches their task picks the
                     nearest-sounding one and finds out three steps in, having already answered
                     questions that do not apply. The description is what makes the choice
                     answerable here rather than by trial.

                     ⚠️ `h-auto` and `whitespace-normal` are LOAD-BEARING, not tidying. A
                     mat-button is a fixed-height, single-line, centred control; without these the
                     second line is clipped and the description never wraps on a narrow screen.
                     `normal-case` likewise — `.button` uppercases, which is fine for a two-word
                     label and hostile to a sentence.

                     🟢 `items-start` with the icon nudged down a hair, rather than `items-center`.
                     Centring against a two-line block floats the glyph into the gap between the
                     lines and reads as belonging to neither; aligned to the title it reads as
                     labelling the row. -->
                <button mat-button
                  class="button justify-start items-start h-auto py-10 text-left normal-case whitespace-normal border-2 border-solid {{isNavItemDisabled(navItem) ? 'disabled bg-gray-lighter border-transparent' : selectedWorkflowNavItem?.order === navItem.order ? 'bg-white border-mint-darker' : 'border-transparent'}} {{navItem.buttonClass}} is-inverted flex"
                  (click)="chooseNavItem(navItem)">
                  <i class="mt-2 mr-15 material-icons mi-22 shrink-0" [style.transform]="navItem.iconTransform" [textContent]="navItem.icon"></i>
                  <span class="flex flex-col items-start gap-y-2">
                    <span class="flex items-center gap-x-10">
                      <!-- Bold on every row, not only the selected one. Selection is already said
                           twice (the white lift and the mint border); a weight change on top of
                           that made the unselected rows read as secondary to a choice nobody had
                           made yet. -->
                      <span class="font-bold leading-tight" [textContent]="navItem.title"></span>
                      <!-- Per-row badge only when there is no heading to carry it — i.e. an advisor
                           with V2 permissions and nothing classic left to contrast against. -->
                      @if (navItem.isNew && !isNavItemDisabled(navItem) && !showNavGroupHeadings) {
                        <span class="px-8 text-xs font-bold tracking-wide uppercase rounded-full whitespace-nowrap bg-mint-lighter text-mint-darkest" [textContent]="navItem.newLabel ?? 'New Experience!'"></span>
                      }
                    </span>
                    <!-- ⚠️ Deliberately NOT mint. Inheriting the row's link colour would give the
                         description the same visual weight as the title and the panel would read
                         as six equal lines. Grey is what keeps the title scannable. -->
                    @if (navItem.description) {
                      <span class="text-xs font-normal leading-snug text-gray-dark" [textContent]="navItem.description"></span>
                    }
                  </span>
                </button>
              }
            }
          </div>

          <!-- No heading over this group. The mint fill above already draws the boundary, so the
               separation is carried by the top margin instead — and it is conditional on the same
               flag the heading was, so an advisor with no V2 permissions still gets one flat list
               with no stray gap under "Select the type of request:". -->
          <div class="flex flex-col w-full gap-y-5 mb-15" [class.mt-15]="showNavGroupHeadings">
            @for (navItem of classicNavItems; track navItem) {
              @if (navItem.order !== requestTypeEnum.planningDesk) {
                <!-- `border-2 border-solid` mirrors the mint rows so both groups sit at the same
                     row height and nothing steps in or out down the list. `border-transparent` is
                     kept OUT of the selected branch so it cannot override the border colour
                     `is-outlined` applies. -->
                <button mat-button
                  class="button justify-start border-2 border-solid {{isNavItemDisabled(navItem) ? 'disabled bg-gray-lighter border-transparent' : selectedWorkflowNavItem?.order === navItem.order ? 'is-outlined bg-mint-lightest font-bold' : 'border-transparent'}} {{navItem.buttonClass}} is-inverted font-semibold flex items-center"
                  [matTooltip]="navItemTooltip(navItem)" matTooltipPosition="right"
                  matTooltipShowDelay="1000" (click)="chooseNavItem(navItem)">
                  <i class="mr-20 material-icons mi-22" [style.transform]="navItem.iconTransform" [textContent]="navItem.icon"></i>
                  <span [textContent]="navItem.title"></span>
                </button>
              }
              <!-- <button mat-button class="button {{!selectedAccountId ? 'disabled bg-gray-lighter' : navItem.buttonClass}} is-inverted font-semibold flex items-center {{ selectedWorkflowNavItem?.title === navItem.title ? 'is-outlined bg-mint-lightest font-bold' : ''}}"
              [matTooltip]="'Click to go to ' + navItem.title.toLowerCase()" matTooltipPosition="right"
              matTooltipShowDelay="1000" (click)="selectedWorkflow = navItem.order; selectedWorkflowNavItem = navItem"
              *ngIf="navItem.order === requestTypeEnum.planningDesk">
              <i class="mr-20 material-icons mi-20" [textContent]="navItem.icon"></i>
              <span [textContent]="navItem.title"></span>
            </button> -->
          }
        </div>
        }
        <!-- Closes the wrapper opened above the request list. -->
        </div>
    </div>
  </section>
  <!-- The picker has no footer at all. A click on a request IS the answer, so there is nothing
       here to press — and a bar holding a permanently disabled Next was part of what made this
       screen read as unfinished. The Back/Next pair that lived here belonged to the removed
       second step. -->
}
@if (selectedWorkflowNavItem && selectedWorkflow !== requestTypeEnum.accountSelection) {
  @if (account) {
    @if (selectedWorkflow === requestTypeEnum.contribution) {
      <app-contribution-request [account]="account" [workflow]="selectedWorkflowNavItem" [solicitation]="selectedSolicitationOption" (canceled)="resetEvent()"
      @fadeIn></app-contribution-request>
    }
    @else if (selectedWorkflow === requestTypeEnum.distribution) {
      <app-distribution-request [account]="account" [workflow]="selectedWorkflowNavItem" [solicitation]="selectedSolicitationOption" (canceled)="resetEvent()"
      @fadeIn></app-distribution-request>
    }
    @else if (selectedWorkflow === requestTypeEnum.reallocation) {
      <app-reallocation-request [account]="account" [workflow]="selectedWorkflowNavItem" [solicitation]="selectedSolicitationOption" (canceled)="resetEvent()"
      @fadeIn></app-reallocation-request>
    }
    @else if (selectedWorkflow === requestTypeEnum.reallocationV2) {
      <app-reallocation-v2-request [account]="account" [workflow]="selectedWorkflowNavItem" [solicitation]="selectedSolicitationOption" (canceled)="resetEvent()"
      @fadeIn></app-reallocation-v2-request>
    }
    <!--
      Contribution V2 and Distribution V2 open the SAME wizard as reallocationV2 and
      initialAccountSetup below. Four branches, one component, on purpose: the wizard reads
      `workflow.order` to pick its mode, so the enum member is what tells it which request it
      is filing. Close Account has no branch of its own — it is a checkbox on the distribution's
      first step, not a request type.
    -->
    @else if (selectedWorkflow === requestTypeEnum.contributionV2) {
      <app-reallocation-v2-request [account]="account" [workflow]="selectedWorkflowNavItem" [solicitation]="selectedSolicitationOption" (canceled)="resetEvent()"
      @fadeIn></app-reallocation-v2-request>
    }
    @else if (selectedWorkflow === requestTypeEnum.distributionV2) {
      <app-reallocation-v2-request [account]="account" [workflow]="selectedWorkflowNavItem" [solicitation]="selectedSolicitationOption" (canceled)="resetEvent()"
      @fadeIn></app-reallocation-v2-request>
    }
    @else if (selectedWorkflow === requestTypeEnum.closeAccount) {
      <app-close-account-request [account]="account" [workflow]="selectedWorkflowNavItem" [solicitation]="selectedSolicitationOption" (canceled)="resetEvent()"
      @fadeIn></app-close-account-request>
    }
    @else if (selectedWorkflow === requestTypeEnum.generalAccount) {
      <app-general-account-request  [account]="account" [workflow]="selectedWorkflowNavItem" (canceled)="resetEvent()"></app-general-account-request>
    }
    <!--
      Initial Account Setup opens the REALLOCATION wizard, which files both request types.
      It reads the `workflow` input this branch already passed to tell which mode it is in.

      The separate Initial Account wizard this used to point at was deleted in ACMTECH-918;
      the two branches being identical is the point, not an oversight. Both request types are
      one wizard now, and the enum member is still what tells it which mode to open in.
    -->
    @else if (selectedWorkflow === requestTypeEnum.initialAccountSetup) {
      <app-reallocation-v2-request [account]="account" [workflow]="selectedWorkflowNavItem" [solicitation]="selectedSolicitationOption" (canceled)="resetEvent()"
      @fadeIn></app-reallocation-v2-request>
    }
  }
  @if (selectedWorkflow===requestTypeEnum.planningDesk) {
    <app-planning-desk-request [workflow]="selectedWorkflowNavItem" (canceled)="resetEvent()"
      @fadeIn>
    </app-planning-desk-request>
  }
}
</div>